import { useApolloClient } from '@apollo/client/react';
import { Auth } from 'aws-amplify';
import DOMPurifyFactory from 'dompurify';
import { motion } from 'framer-motion';
import { marked } from 'marked';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation, useNavigate } from 'react-router-dom';

import PlusIcon from '~/assets/PlusIcon';
import SendIcon from '~/assets/SendIcon';
import {
  computeCurrentLocationDeeplink,
  createAssistantMessage,
  createErrorMessage,
  createUserMessage,
  MESSAGES_PER_PAGE,
  removeLastErrorMessage,
  SCROLL_THRESHOLD,
} from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { TriggerPayload, useBedrockStream } from '~/components/AIInsightsModal/useBedrockStream';
import { useTypewriter } from '~/components/AIInsightsModal/useTypewriter';
import { visitIQAiInsightsSidebarWidth } from '~/constants/localStorage';
import { useWindowDimensions } from '~/hooks/useDimensions';
import {
  ConversationMessage,
  ConversationRole,
  GetConversationsDocument,
  GetConversationsQuery,
  useConversationMessagesQuery,
  useCreateConversationMutation,
  useGetConversationsQuery,
  useTouchConversationAfterUserMessageMutation,
} from '~/types/api.graphql';

import AddAudiencePopover from './AddAudiencePopover';
import {
  AddAudienceButton,
  AudienceMobileOverlay,
  ButtonWrapper,
  ChatContainer,
  CloseButton,
  ConversationCountBadge,
  ConversationsDropdownWrapper,
  ConversationsPopoverFooter,
  ConversationsPopoverGradient,
  ConversationsPopoverItem,
  ConversationsPopoverItemTitle,
  ConversationsPopoverList,
  ConversationsPopoverListWrapper,
  ConversationsPopoverPanel,
  EmptyStateContainer,
  EmptyStateSubtitle,
  EmptyStateTitle,
  InputActionsRow,
  InputContainer,
  InputWrapper,
  LoadingContainer,
  LoadingDot,
  LoadingDotsContainer,
  LoadingMoreContainer,
  LoadingSubtitle,
  LoadingTitle,
  MarkdownContent,
  MessageBubble,
  MessageContainer,
  MessagesList,
  MessagesListWrapper,
  MessagesScrollGradient,
  MobileBottomSheetContent,
  NewConversationIconButton,
  PrivacyDisclaimer,
  ResizeHandle,
  SendButton,
  SidebarHeader,
  SidebarInner,
  SidebarTitleContainer,
  SidebarTitleText,
  SidebarWrapper,
  StyledBottomSheet,
  SuggestedActionsContainer,
  ToolStatusContainer,
  ToolStatusDot,
  ToolStatusText,
  UserMessageBubble,
} from './AIInsightsSidebar.styles';
import {
  CONVERSATION_MAX_TITLE_LENGTH,
  ConversationListItem,
  CONVERSATIONS_PER_PAGE,
  wrapTablesForScroll,
} from './AIInsightsSidebar.utils';
import { MIN_SIDEBAR_WIDTH, useAIInsightsSidebar } from './AIInsightsSidebarContext';
import AnimatedInputBorder from './AnimatedInputBorder';
import AudienceMentionInput from './AudienceMentionInput';
import AudiencePickerContent from './AudiencePickerContent';
import { renderBodyWithChipLinks, stripMentionMarkdown } from './mentionChipUtils';
import SuggestedActionItem from './SuggestedActionItem';
import { useAIMentionInput } from './useAIMentionInput';
import { useConversationReferenceMentions } from './useConversationReferenceMentions';
import { useToolStatus } from './useToolStatus';

// Explicitly create instance with current window to avoid issues when the
// module-level `createDOMPurify()` runs before DOM is fully ready
const DOMPurify = DOMPurifyFactory(window);

// DOMPurify options shared by all message bubbles that render mention chips.
// The extra tags/attrs allow SVG icons inside chips to survive sanitisation.
const CHIP_DOMPURIFY_OPTIONS = {
  ADD_TAGS: ['svg', 'path', 'circle'],
  ADD_ATTR: [
    'stroke',
    'stroke-width',
    'stroke-linecap',
    'stroke-linejoin',
    'stroke-dasharray',
    'fill-rule',
    'viewBox',
    'xmlns',
    'aria-hidden',
  ],
};

const AIInsightsSidebar = () => {
  const { t } = useTranslation();
  const { isInMobile } = useWindowDimensions();
  // Context is always present here since AIInsightsSidebar is rendered inside the provider
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ctx = useAIInsightsSidebar()!;
  const {
    isOpen,
    section,
    entityDeepLink,
    conversationId,
    closeSidebar,
    sidebarWidth,
    maxSidebarWidth,
    setSidebarWidth,
    isResizing,
    setIsResizing,
    setConversationId,
    clickCounterCTA,
  } = ctx;

  const location = useLocation();
  const navigate = useNavigate();

  const {
    editorRef,
    editorCallbackRef,
    mentionQuery,
    hasContent,
    handleInput,
    handleKeyDown: handleMentionKeyDown,
    handleCopy,
    handleCut,
    handlePaste,
    handleEditorMouseDown,
    insertMention,
    insertReferenceAtCaret,
    saveCaretPosition,
    closeMention,
    serialize,
    reset: resetEditor,
    startTypingPrefill,
  } = useAIMentionInput({
    section,
    animateTyping: true,
  });

  const { results: mentionResults, loading: isLoadingMentions } = useConversationReferenceMentions({
    query: mentionQuery,
  });

  // ---------------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------------
  const [isAudiencePopoverOpen, setIsAudiencePopoverOpen] = useState(false);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showPopoverTopGradient, setShowPopoverTopGradient] = useState(false);
  const [showPopoverBottomGradient, setShowPopoverBottomGradient] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, right: 0 });
  const [showMessagesTopGradient, setShowMessagesTopGradient] = useState(false);
  const [showMessagesBottomGradient, setShowMessagesBottomGradient] = useState(false);

  // ---------------------------------------------------------------------------
  // Open animation state
  // ---------------------------------------------------------------------------
  const [openAnimKey, setOpenAnimKey] = useState(0);
  type BorderPhase = 'hidden' | 'drawing' | 'visible';
  const [borderPhase, setBorderPhase] = useState<BorderPhase>('visible');
  const [inputInternalsVisible, setInputInternalsVisible] = useState(true);

  // ---------------------------------------------------------------------------
  // Resize handler
  // ---------------------------------------------------------------------------
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX: number = e.clientX;
      const startWidth: number = sidebarWidth;
      setIsResizing(true);

      const onMouseMove = (ev: MouseEvent) => {
        const delta: number = startX - ev.clientX; // drag left → wider
        setSidebarWidth(Math.max(MIN_SIDEBAR_WIDTH, Math.min(maxSidebarWidth, startWidth + delta)));
      };

      const onMouseUp = (ev: MouseEvent) => {
        const finalWidth: number = Math.max(
          MIN_SIDEBAR_WIDTH,
          Math.min(maxSidebarWidth, startWidth + (startX - ev.clientX)),
        );
        localStorage.setItem(visitIQAiInsightsSidebarWidth, `${finalWidth}`);
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [sidebarWidth, maxSidebarWidth, setSidebarWidth, setIsResizing],
  );

  // ---------------------------------------------------------------------------
  // Chat state
  // ---------------------------------------------------------------------------
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState<string[]>([]);

  // ---------------------------------------------------------------------------
  // Refs
  // ---------------------------------------------------------------------------
  const conversationsDropdownRef = useOnclickOutside(() => setIsPopoverOpen(false));
  const popoverListRef = useRef<HTMLDivElement>(null);
  const conversationBadgeRef = useRef<HTMLButtonElement>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const streamingBufferRef = useRef<string>('');
  const displayedLengthRef = useRef<number>(0);
  const isInitialLoadRef = useRef(true);
  const isLocalSessionRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const scrollHeightBeforeLoadRef = useRef(0);
  const isSidebarOpenRef = useRef(false);
  const userScrolledUpRef = useRef(false);
  const shouldAnimateRef = useRef(false);

  const [hasReceivedText, setHasReceivedText] = useState(false);

  // ---------------------------------------------------------------------------
  // Queries & mutations
  // ---------------------------------------------------------------------------
  const {
    data: conversationsData,
    loading: isLoadingConversations,
    fetchMore: fetchMoreConversations,
  } = useGetConversationsQuery({
    variables: { first: CONVERSATIONS_PER_PAGE },
    skip: !isOpen,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data,
    loading: isLoadingMessages,
    fetchMore: fetchMoreMessages,
  } = useConversationMessagesQuery({
    variables: {
      conversationId,
      first: MESSAGES_PER_PAGE,
    },
    skip: !isOpen || !conversationId,
    fetchPolicy: 'network-only',
  });

  const apolloClient = useApolloClient();
  const [createConversationMutation] = useCreateConversationMutation();
  const [touchConversationMutation] = useTouchConversationAfterUserMessageMutation();

  const moveConversationToTop = useCallback(
    (convId: string) => {
      const cached = apolloClient.readQuery<GetConversationsQuery>({
        query: GetConversationsDocument,
        variables: { first: CONVERSATIONS_PER_PAGE },
      });
      if (!cached) return;
      const nodes = cached.conversations.nodes;
      const idx = nodes.findIndex((c) => c.id === convId);
      if (idx <= 0) return; // already first or not found
      apolloClient.writeQuery<GetConversationsQuery>({
        query: GetConversationsDocument,
        variables: { first: CONVERSATIONS_PER_PAGE },
        data: {
          ...cached,
          conversations: {
            ...cached.conversations,
            nodes: [nodes[idx], ...nodes.slice(0, idx), ...nodes.slice(idx + 1)],
          },
        },
      });
    },
    [apolloClient],
  );

  const hasMoreMessages = data?.conversationMessages?.pageInfo?.hasNextPage ?? false;
  const messagesEndCursor = data?.conversationMessages?.pageInfo?.endCursor;
  const agentSessionId = data?.conversationMessages?.agentSessionId;

  const totalConversationCount = conversationsData?.conversations?.totalCount ?? 0;
  const hasMoreConversations = conversationsData?.conversations?.pageInfo?.hasNextPage ?? false;
  const conversationsEndCursor = conversationsData?.conversations?.pageInfo?.endCursor;

  // ---------------------------------------------------------------------------
  // Derived conversation title (shown in header)
  // ---------------------------------------------------------------------------
  const currentConversationTitle = useMemo(
    () =>
      conversationsData?.conversations?.nodes?.find((c) => c.id === conversationId)?.title ??
      t('aiInsights.newConversation'),
    [conversationId, conversationsData, t],
  );

  // ---------------------------------------------------------------------------
  // Scroll helpers
  // ---------------------------------------------------------------------------
  const scrollToBottom = useCallback((force = false) => {
    if (messagesListRef.current && (force || !userScrolledUpRef.current)) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  }, []);

  const updateMessagesGradients = useCallback(() => {
    const el = messagesListRef.current;
    if (!el) return;
    setShowMessagesTopGradient(el.scrollTop > 4);
    setShowMessagesBottomGradient(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.deltaY < 0) {
      userScrolledUpRef.current = true;
    } else if (e.deltaY > 0 && messagesListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        userScrolledUpRef.current = false;
      }
    }
  }, []);

  const maintainScrollPosition = useCallback((previousScrollHeight: number) => {
    setTimeout(() => {
      if (messagesListRef.current) {
        const newScrollHeight = messagesListRef.current.scrollHeight;
        messagesListRef.current.scrollTop = newScrollHeight - previousScrollHeight;
      }
      isLoadingMoreRef.current = false;
    }, 0);
  }, []);

  // ---------------------------------------------------------------------------
  // Tool status helpers
  // ---------------------------------------------------------------------------
  const { toolStatus, toolStatusVisible, handleToolStatus: handleToolStatusBase, resetToolStatus } = useToolStatus();

  const handleToolStatus = useCallback(
    (status: string | null) => {
      if (status === null) {
        shouldAnimateRef.current = true;
        // flushSync forces a synchronous re-render so the element becomes visible right away
        // instead of waiting for React to flush the batched updates after the stream ends
        flushSync(() => {
          setHasReceivedText(true);
        });
      }
      handleToolStatusBase(status);
    },
    [handleToolStatusBase],
  );

  const streamingRefCallback = useCallback((node: HTMLDivElement | null) => {
    streamingMessageRef.current = node;
    if (node && streamingBufferRef.current.length > 0) {
      node.innerHTML = wrapTablesForScroll(DOMPurify.sanitize(marked.parse(streamingBufferRef.current) as string));
      displayedLengthRef.current = streamingBufferRef.current.length;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------
  const { startTypewriter, stopTypewriter, waitForTypewriterCompletion } = useTypewriter({
    streamingBufferRef,
    displayedLengthRef,
    streamingMessageRef,
    scrollToBottom,
    shouldAnimateRef,
  });

  const currentLocationDeeplink = computeCurrentLocationDeeplink(entityDeepLink, location.pathname);

  const { sendMessage: sendBedrockMessage } = useBedrockStream({
    sessionIdRef,
    abortControllerRef,
    streamingBufferRef,
    currentLocationDeeplink,
    onToolStatus: handleToolStatus,
  });

  // ---------------------------------------------------------------------------
  // Reset helpers
  // ---------------------------------------------------------------------------
  const resetChatState = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    stopTypewriter();
    resetToolStatus();
    setIsSending(false);
    setIsStreaming(false);
    setHasReceivedText(false);
    shouldAnimateRef.current = false;
    streamingBufferRef.current = '';
    displayedLengthRef.current = 0;
    resetEditor();
    setMessages([]);
    setSuggestedActions([]);
    sessionIdRef.current = null;
    isInitialLoadRef.current = true;
    isLocalSessionRef.current = false;
    userScrolledUpRef.current = false;
  }, [stopTypewriter, resetToolStatus, resetEditor]);

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------
  const handleSelectConversation = useCallback(
    (conv: ConversationListItem) => {
      // If already on this conversation just close the popover — don't reset
      if (conv.id === conversationId) {
        setIsPopoverOpen(false);
        setTimeout(() => editorRef.current?.focus(), 0);
        return;
      }
      resetChatState();
      setConversationId(conv.id);
      setIsPopoverOpen(false);
    },
    [conversationId, resetChatState, setConversationId],
  );

  const handleNewConversation = useCallback(() => {
    resetChatState();
    setConversationId(null);
    setIsPopoverOpen(false);
    setTimeout(() => editorRef.current?.focus(), 0);
  }, [resetChatState, setConversationId]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Reset when sidebar closes; kick off opening animations when it opens
  useEffect(() => {
    if (isOpen) {
      isSidebarOpenRef.current = true;
      setOpenAnimKey((k) => k + 1);
      setBorderPhase('hidden');
      setInputInternalsVisible(false);

      const t = setTimeout(() => {
        setBorderPhase('drawing');
        setInputInternalsVisible(true);
      }, 200);

      return () => clearTimeout(t);
    }

    isSidebarOpenRef.current = false;
    resetChatState();
    setIsPopoverOpen(false);
    setIsAudiencePopoverOpen(false);
    setBorderPhase('visible');
    setInputInternalsVisible(true);
    return undefined;
  }, [isOpen, resetChatState]);

  // Load messages into state when query data arrives
  useEffect(() => {
    if (!isOpen || !data?.conversationMessages?.nodes) return;
    // Skip when the conversation was created locally this session — messages are managed by the stream
    if (isLocalSessionRef.current) return;

    const apiMessages = data.conversationMessages.nodes as ConversationMessage[];
    const chronologicalMessages = [...apiMessages].reverse();
    const wasLoadingMore = isLoadingMoreRef.current;
    const previousScrollHeight = scrollHeightBeforeLoadRef.current;

    setMessages(chronologicalMessages);

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      setTimeout(() => {
        scrollToBottom(true);
        updateMessagesGradients();
      }, 50);
    } else if (wasLoadingMore) {
      maintainScrollPosition(previousScrollHeight);
    }
    setTimeout(updateMessagesGradients, 0);
  }, [isOpen, data, scrollToBottom, maintainScrollPosition, updateMessagesGradients]);

  // Sync agentSessionId from query result into ref
  useEffect(() => {
    if (agentSessionId) {
      sessionIdRef.current = agentSessionId;
      setTimeout(() => editorRef.current?.focus(), 0);
    }
  }, [agentSessionId]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleLoadMore = useCallback(async () => {
    if (!hasMoreMessages || isLoadingMessages || isLoadingMore || !messagesEndCursor) return;

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    scrollHeightBeforeLoadRef.current = messagesListRef.current?.scrollHeight ?? 0;

    try {
      await fetchMoreMessages({
        variables: { after: messagesEndCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.conversationMessages) return prev;
          return {
            ...prev,
            conversationMessages: {
              ...fetchMoreResult.conversationMessages,
              // preserve agentSessionId — fetchMore pages return null for it
              agentSessionId: prev.conversationMessages.agentSessionId,
              nodes: [
                ...(prev.conversationMessages.nodes ?? []),
                ...(fetchMoreResult.conversationMessages.nodes ?? []),
              ],
            },
          };
        },
      });
    } catch {
      isLoadingMoreRef.current = false;
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreMessages, isLoadingMessages, isLoadingMore, messagesEndCursor, fetchMoreMessages]);

  const handleScroll = useCallback(() => {
    if (!messagesListRef.current || isLoadingMore) return;
    if (messagesListRef.current.scrollTop < SCROLL_THRESHOLD && hasMoreMessages) {
      void handleLoadMore();
    }
    updateMessagesGradients();
  }, [hasMoreMessages, handleLoadMore, isLoadingMore, updateMessagesGradients]);

  const updatePopoverGradients = useCallback(() => {
    const el = popoverListRef.current;
    if (!el) return;
    setShowPopoverTopGradient(el.scrollTop > 0);
    setShowPopoverBottomGradient(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  const loadMoreConversations = useCallback(() => {
    if (!conversationsEndCursor) return;
    void fetchMoreConversations({
      variables: { first: CONVERSATIONS_PER_PAGE, after: conversationsEndCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.conversations) return prev;
        return {
          ...prev,
          conversations: {
            ...fetchMoreResult.conversations,
            nodes: [...prev.conversations.nodes, ...fetchMoreResult.conversations.nodes],
          },
        };
      },
    });
  }, [conversationsEndCursor, fetchMoreConversations]);

  // Recompute gradients when the popover opens or its data changes
  useEffect(() => {
    if (!isPopoverOpen) return;
    // Use rAF to let the DOM paint first
    requestAnimationFrame(updatePopoverGradients);
  }, [isPopoverOpen, conversationsData, updatePopoverGradients]);

  const startStream = useCallback(
    async (payload: TriggerPayload, displayText?: string) => {
      if (isSending || isStreaming) return;

      let shouldHitTouchMutation = true;

      // For new conversations (no conversationId yet): create the backend record on first send.
      // Title is seeded from the first user message excerpt.
      // Session ID = `${userId}-${conversationId}` per server-side getSessionId().
      let activeConversationId = conversationId;
      if (!conversationId && !sessionIdRef.current) {
        try {
          shouldHitTouchMutation = false;

          // Get session first — if it fails we haven't created anything yet
          const session = await Auth.currentSession();
          const trimmed = displayText?.trim();
          const titleText = trimmed ? stripMentionMarkdown(trimmed) : undefined;
          const excerpt = titleText
            ? titleText.slice(0, CONVERSATION_MAX_TITLE_LENGTH).trimEnd()
            : t('aiInsights.newConversation');
          const title = titleText && titleText.length > CONVERSATION_MAX_TITLE_LENGTH ? `${excerpt}...` : excerpt;
          const result = await createConversationMutation({ variables: { title } });
          const newConv = result.data?.createConversation;
          if (!newConv) throw new Error('Failed to create conversation');
          // decodePayload() returns an any-typed map; cast explicitly to satisfy lint
          const userId = String(session.getIdToken().decodePayload().sub as unknown);
          sessionIdRef.current = userId.concat('-', newConv.id);
          isLocalSessionRef.current = true;
          setConversationId(newConv.id);
          activeConversationId = newConv.id;
          // Prepend new conversation to cache so it appears in the list immediately
          const cached = apolloClient.readQuery<GetConversationsQuery>({
            query: GetConversationsDocument,
            variables: { first: CONVERSATIONS_PER_PAGE },
          });
          if (cached) {
            apolloClient.writeQuery<GetConversationsQuery>({
              query: GetConversationsDocument,
              variables: { first: CONVERSATIONS_PER_PAGE },
              data: {
                ...cached,
                conversations: {
                  ...cached.conversations,
                  nodes: [newConv, ...cached.conversations.nodes],
                  totalCount: cached.conversations.totalCount + 1,
                },
              },
            });
          }
        } catch {
          setMessages((prev) => [...prev, createErrorMessage(t('aiInsights.errorSendingMessage'))]);
          return;
        }
      }

      if (!sessionIdRef.current) return;

      resetEditor();
      setIsSending(true);
      setIsStreaming(true);
      setSuggestedActions([]);
      setHasReceivedText(false);
      shouldAnimateRef.current = false;
      streamingBufferRef.current = '';
      displayedLengthRef.current = 0;
      userScrolledUpRef.current = false;
      startTypewriter();

      if (displayText) {
        setMessages((prev) => [...removeLastErrorMessage(prev), createUserMessage(displayText)]);
      }
      setTimeout(() => scrollToBottom(true), 0);

      abortControllerRef.current = new AbortController();

      try {
        setIsSending(false);

        if (activeConversationId) {
          moveConversationToTop(activeConversationId);
          if (shouldHitTouchMutation) {
            void touchConversationMutation({ variables: { conversationId: activeConversationId } });
          }
        }

        const result = await sendBedrockMessage(payload);

        if (streamingBufferRef.current.length > 0) {
          await waitForTypewriterCompletion();
        }

        if (result.text) {
          setMessages((prev) => [...prev, createAssistantMessage(result.text)]);
        }
        if (result.actions.length > 0) {
          setSuggestedActions(result.actions);
          requestAnimationFrame(() => {
            setTimeout(() => scrollToBottom(true), 100);
          });
        }
      } catch (error: unknown) {
        if ((error as Error).name === 'AbortError') return;
        setMessages((prev) => [...prev, createErrorMessage(t('aiInsights.errorSendingMessage'))]);
      } finally {
        stopTypewriter();
        resetToolStatus();
        setIsSending(false);
        setIsStreaming(false);
        setHasReceivedText(false);
        shouldAnimateRef.current = false;
        streamingBufferRef.current = '';
        displayedLengthRef.current = 0;
        abortControllerRef.current = null;
        setTimeout(() => editorRef.current?.focus(), 0);
      }
    },
    [
      isSending,
      isStreaming,
      conversationId,
      createConversationMutation,
      touchConversationMutation,
      moveConversationToTop,
      t,
      setConversationId,
      startTypewriter,
      stopTypewriter,
      sendBedrockMessage,
      waitForTypewriterCompletion,
      scrollToBottom,
      resetToolStatus,
      resetEditor,
    ],
  );

  // Tracks whether the sidebar was open on the previous render, to distinguish
  // "first open" (border animation handles prefill) from "re-click while open".
  const prevIsOpenRef = useRef(isOpen);
  const borderPhaseRef = useRef(borderPhase);
  useEffect(() => {
    borderPhaseRef.current = borderPhase;
  }, [borderPhase]);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    // Only act when CTA was clicked while sidebar was already open — first-open
    // case is handled by handleBorderAnimComplete via the border animation.
    if (!wasOpen || !isOpen) return;
    // If the draw animation is still running, handleBorderAnimComplete will
    // call startTypingPrefill when it finishes — don't call it twice.
    if (borderPhaseRef.current !== 'visible') return;
    setTimeout(() => startTypingPrefill(), 0);
  }, [clickCounterCTA, isOpen, startTypingPrefill]);

  const handleBorderAnimComplete = useCallback(() => {
    setBorderPhase('visible');
    setTimeout(() => startTypingPrefill(), 100);
  }, [startTypingPrefill]);

  const handleSendMessage = useCallback(async () => {
    const prompt = serialize();
    if (!prompt) return;
    await startStream({ prompt }, prompt);
  }, [serialize, startStream]);

  const handleActionClick = useCallback(
    async (action: string) => {
      await startStream({ prompt: action }, action);
    },
    [startStream],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      handleMentionKeyDown(e, { onSubmit: () => void handleSendMessage() });
    },
    [handleMentionKeyDown, handleSendMessage],
  );

  // Intercept clicks on mention chip links inside message bubbles so
  // navigation is handled by React Router (SPA, no full-page reload).
  const handleMessageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const chip = (e.target as HTMLElement).closest<HTMLAnchorElement>('a.mention-chip');
      if (!chip) return;
      e.preventDefault();
      const appPath = chip.getAttribute('href');
      if (appPath) navigate(appPath);
    },
    [navigate],
  );

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  // Loading only applies to existing conversations (new conversations start empty immediately)
  const isInitialLoading = !!conversationId && !isLoadingMore && (isLoadingMessages || !agentSessionId);
  const isEmpty = !isInitialLoading && messages.length === 0 && !isStreaming;
  // Input/send are disabled only while waiting for an existing conversation's session
  const isSessionPending = !!conversationId && !agentSessionId;

  const renderLoadingState = () => (
    <LoadingContainer>
      <LoadingTitle>{t('aiInsights.loadingMessages')}</LoadingTitle>
      <LoadingSubtitle>{t('aiInsights.loadingMessagesSubtitle')}</LoadingSubtitle>
      <LoadingDotsContainer>
        {[...Array(6)].map((_, i) => (
          <LoadingDot key={i} $delay={i * 0.2} />
        ))}
      </LoadingDotsContainer>
    </LoadingContainer>
  );

  const renderEmptyState = () => (
    <EmptyStateContainer>
      <motion.div
        key={`empty-title-${openAnimKey}`}
        initial={{ opacity: 0, x: -16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.25, ease: 'easeOut' }}
      >
        <EmptyStateTitle>{t('aiInsights.emptyStateTitle')}</EmptyStateTitle>
      </motion.div>
      <motion.div
        key={`empty-subtitle-${openAnimKey}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.75, ease: 'easeOut' }}
      >
        <EmptyStateSubtitle>{t('aiInsights.emptyStateSubtitle')}</EmptyStateSubtitle>
      </motion.div>
    </EmptyStateContainer>
  );

  const renderLoadingMore = () => (
    <LoadingMoreContainer>
      <LoadingDotsContainer>
        {[...Array(3)].map((_, i) => (
          <LoadingDot key={i} $delay={i * 0.2} />
        ))}
      </LoadingDotsContainer>
    </LoadingMoreContainer>
  );

  const renderMessages = () => (
    <>
      {isLoadingMore && renderLoadingMore()}
      {messages.map((message: ConversationMessage) => (
        <React.Fragment key={message.id}>
          <MessageContainer $isUser={message.role === ConversationRole.User}>
            {message.role === ConversationRole.User ? (
              <UserMessageBubble>
                <MarkdownContent
                  dangerouslySetInnerHTML={{
                    __html: wrapTablesForScroll(
                      DOMPurify.sanitize(
                        marked.parse(renderBodyWithChipLinks(message.body ?? '')) as string,
                        CHIP_DOMPURIFY_OPTIONS,
                      ),
                    ),
                  }}
                />
              </UserMessageBubble>
            ) : (
              <MessageBubble>
                <MarkdownContent
                  dangerouslySetInnerHTML={{
                    __html: wrapTablesForScroll(
                      DOMPurify.sanitize(
                        marked.parse(renderBodyWithChipLinks(message.body ?? '')) as string,
                        CHIP_DOMPURIFY_OPTIONS,
                      ),
                    ),
                  }}
                />
              </MessageBubble>
            )}
          </MessageContainer>
        </React.Fragment>
      ))}
      {isStreaming && (
        <MessageContainer>
          <MessageBubble>
            {/* Always mounted so the typewriter can write to it; hidden until first text arrives */}
            <MarkdownContent ref={streamingRefCallback} style={hasReceivedText ? undefined : { display: 'none' }} />
            {/* Thinking indicator: initial state before text, or below accumulated text mid-stream */}
            {(!hasReceivedText || toolStatus) && (
              <ToolStatusContainer>
                <ToolStatusDot />
                <ToolStatusText $visible={!toolStatus || toolStatusVisible}>
                  {toolStatus ?? t('aiInsights.thinking')}
                </ToolStatusText>
              </ToolStatusContainer>
            )}
          </MessageBubble>
        </MessageContainer>
      )}
    </>
  );

  const shouldShowSuggestedActions = !isStreaming && suggestedActions.length > 0;

  const renderChatContent = () => {
    if (isInitialLoading) return renderLoadingState();
    if (isEmpty) return renderEmptyState();
    return renderMessages();
  };

  const renderConversationsPopover = () => {
    const convs = conversationsData?.conversations?.nodes ?? [];
    return (
      <ConversationsDropdownWrapper ref={conversationsDropdownRef}>
        <ConversationCountBadge
          ref={conversationBadgeRef}
          type="button"
          aria-label={t('aiInsights.conversations')}
          onClick={() => {
            if (!isPopoverOpen && conversationBadgeRef.current) {
              const rect = conversationBadgeRef.current.getBoundingClientRect();
              setPopoverPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
            }
            setIsPopoverOpen((prev) => !prev);
          }}
        >
          {totalConversationCount}
        </ConversationCountBadge>
        <ConversationsPopoverPanel $isOpen={isPopoverOpen} $top={popoverPos.top} $right={popoverPos.right}>
          <ConversationsPopoverListWrapper>
            <ConversationsPopoverGradient $visible={showPopoverTopGradient} $position="top" />
            <ConversationsPopoverList
              id="conversationsPopoverList"
              ref={popoverListRef}
              onScroll={updatePopoverGradients}
            >
              {isLoadingConversations && convs.length === 0 ? (
                <ConversationsPopoverItemTitle as="div" style={{ padding: '0.625rem 0.875rem' }}>
                  {t('aiInsights.loading')}
                </ConversationsPopoverItemTitle>
              ) : convs.length === 0 ? (
                <ConversationsPopoverItemTitle as="div" style={{ padding: '0.625rem 0.875rem' }}>
                  {t('aiInsights.noConversationsYet')}
                </ConversationsPopoverItemTitle>
              ) : (
                <InfiniteScroll
                  dataLength={convs.length}
                  next={loadMoreConversations}
                  hasMore={hasMoreConversations}
                  loader={null}
                  scrollableTarget="conversationsPopoverList"
                  scrollThreshold="40px"
                  style={{ overflow: 'hidden' }}
                >
                  {convs.map((conv) => (
                    <ConversationsPopoverItem
                      key={conv.id}
                      type="button"
                      onClick={() => handleSelectConversation(conv)}
                    >
                      <ConversationsPopoverItemTitle>{conv.title}</ConversationsPopoverItemTitle>
                    </ConversationsPopoverItem>
                  ))}
                </InfiniteScroll>
              )}
            </ConversationsPopoverList>
            <ConversationsPopoverGradient $visible={showPopoverBottomGradient} $position="bottom" />
          </ConversationsPopoverListWrapper>
          <ConversationsPopoverFooter>{t('aiInsights.conversationStorageDisclaimer')}</ConversationsPopoverFooter>
        </ConversationsPopoverPanel>
      </ConversationsDropdownWrapper>
    );
  };

  // ---------------------------------------------------------------------------
  // Full inner render
  // ---------------------------------------------------------------------------

  const renderInner = (fixedWidth = false) => (
    <SidebarInner $width={fixedWidth ? sidebarWidth : 0}>
      <motion.div
        key={`header-${openAnimKey}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <SidebarHeader>
          <NewConversationIconButton
            type="button"
            onClick={handleNewConversation}
            aria-label={t('aiInsights.newConversation')}
          >
            <PlusIcon />
          </NewConversationIconButton>
          <SidebarTitleContainer>
            <SidebarTitleText>{currentConversationTitle}</SidebarTitleText>
          </SidebarTitleContainer>
          {renderConversationsPopover()}
          <CloseButton onClick={closeSidebar} aria-label={t('common.close')}>
            ✕
          </CloseButton>
        </SidebarHeader>
      </motion.div>
      <ChatContainer>
        <MessagesListWrapper>
          <MessagesScrollGradient $position="top" $visible={showMessagesTopGradient} />
          <MessagesList
            ref={messagesListRef}
            onScroll={handleScroll}
            onWheel={handleWheel}
            onClick={handleMessageClick}
          >
            {renderChatContent()}
          </MessagesList>
          <MessagesScrollGradient $position="bottom" $visible={showMessagesBottomGradient} />
        </MessagesListWrapper>
        {shouldShowSuggestedActions && (
          <SuggestedActionsContainer>
            {suggestedActions.map((action, index) => {
              return (
                <SuggestedActionItem
                  key={`action-${index}`}
                  label={action}
                  onClick={() => void handleActionClick(action)}
                />
              );
            })}
          </SuggestedActionsContainer>
        )}
        <motion.div
          key={`input-container-${openAnimKey}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: inputInternalsVisible ? 1 : 0 }}
          transition={{ duration: 0.5 }}
          style={{ flexShrink: 0 }}
        >
          <InputContainer>
            <InputWrapper ref={inputWrapperRef} $borderHidden={borderPhase !== 'visible'}>
              {borderPhase === 'drawing' && <AnimatedInputBorder onAnimationComplete={handleBorderAnimComplete} />}
              <AudienceMentionInput
                editorRef={editorCallbackRef}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onCopy={handleCopy}
                onCut={handleCut}
                onPaste={handlePaste}
                onMouseDown={(e) => {
                  if (isAudiencePopoverOpen) setIsAudiencePopoverOpen(false);
                  handleEditorMouseDown(e);
                }}
                placeholder={t('aiInsights.inputPlaceholder')}
                disabled={isSending || isSessionPending}
                mentionQuery={mentionQuery}
                mentionResults={mentionResults}
                isLoadingMentions={isLoadingMentions}
                onSelectMention={insertMention}
                onCloseMention={closeMention}
              />
              <InputActionsRow>
                <AddAudienceButton
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    saveCaretPosition();
                  }}
                  onClick={() => setIsAudiencePopoverOpen((prev) => !prev)}
                >
                  <PlusIcon />
                  {t('aiInsights.addAudience')}
                </AddAudienceButton>
                <ButtonWrapper>
                  <SendButton
                    onClick={() => void handleSendMessage()}
                    disabled={!hasContent || isSending || isStreaming || isSessionPending}
                    type="button"
                  >
                    <SendIcon />
                  </SendButton>
                </ButtonWrapper>
              </InputActionsRow>
            </InputWrapper>
          </InputContainer>
          {!isInMobile && (
            <AddAudiencePopover
              anchorRef={inputWrapperRef}
              isOpen={isAudiencePopoverOpen}
              onClose={() => setIsAudiencePopoverOpen(false)}
              onSelectMention={insertReferenceAtCaret}
            />
          )}
        </motion.div>
        <motion.div
          key={`footer-${openAnimKey}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          style={{ flexShrink: 0 }}
        >
          <PrivacyDisclaimer>{t('aiInsights.privacyDisclaimer')}</PrivacyDisclaimer>
        </motion.div>
      </ChatContainer>
    </SidebarInner>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (isInMobile) {
    return (
      <StyledBottomSheet open={isOpen} onDismiss={closeSidebar} scrollLocking={false}>
        <MobileBottomSheetContent>
          {renderInner()}
          {isAudiencePopoverOpen && (
            <AudienceMobileOverlay>
              <AudiencePickerContent
                onSelectMention={(ref) => {
                  insertReferenceAtCaret(ref);
                  setIsAudiencePopoverOpen(false);
                }}
                onClose={() => setIsAudiencePopoverOpen(false)}
                isMobile
              />
            </AudienceMobileOverlay>
          )}
        </MobileBottomSheetContent>
      </StyledBottomSheet>
    );
  }

  return (
    <SidebarWrapper
      $isOpen={isOpen}
      $width={sidebarWidth}
      $isResizing={isResizing}
      onWheel={(e) => e.stopPropagation()}
    >
      <ResizeHandle onMouseDown={handleResizeMouseDown} />
      {renderInner(true)}
    </SidebarWrapper>
  );
};

export default AIInsightsSidebar;
